import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Field, ProgressBar, SelectCard, Chip, Badge, Icon, PageHeader } from "@/components/ui";
import { DatePicker, TimePicker } from "@/components/DatePicker";
import { api } from "@/lib/api";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";

const CARE_TYPES = [
  { v: "companion", t: "Companion support", d: "Companionship, errands, light help" },
  { v: "personal", t: "Personal care support", d: "Bathing, dressing, mobility assistance" },
  { v: "live_in", t: "Live-in support", d: "24-hour in-home support" },
  { v: "skilled_medical", t: "Advanced personal care", d: "Hands-on daily support at a higher level (e.g. skilled / medical needs — details stay with the agency)" },
  { v: "respite", t: "Respite (family relief)", d: "Short-term relief for family" },
  { v: "dementia", t: "Memory support", d: "e.g. Dementia / Alzheimer's — general memory-support category only" },
];
const URGENCY = [
  { v: "within_48h", t: "Within 48 hours", d: "We escalate immediately" },
  { v: "this_week", t: "This week", d: "" }, { v: "this_month", t: "This month", d: "" },
  { v: "researching", t: "Just researching", d: "" },
];
const RELATIONSHIPS = ["parent", "grandparent", "spouse/partner", "sibling", "aunt/uncle", "in-law", "friend/neighbor", "legal guardian", "other"];
const GENDERS = ["female", "male", "other"];
const AGE_BANDS = ["under 65", "65-74", "75-84", "85+"];
const SCHEDULE = ["weekday_days", "weekday_nights", "weekends", "overnight", "live_in", "24/7"];
const BUDGETS = ["Under $2k/week", "$2k–3k/week", "$3k–5k/week", "$5k+/week", "Not sure"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS_PER_WEEK = ["Under 10", "10–20", "20–40", "40+", "Live-in / 24-7", "Not sure"];
const LANGUAGES = ["English", "Spanish", "Mandarin", "Tagalog", "French", "Creole", "Russian", "Korean", "Other"];
const CHARACTERISTICS = ["Patient", "Experienced with seniors", "Great communicator", "Physically strong", "Calm & gentle", "Punctual", "Non-smoker", "Comfortable with pets", "Female caregiver", "Male caregiver"];
const EXPERIENCE_LEVELS = ["No preference", "Some experience (1+ yrs)", "Experienced (3+ yrs)", "Highly experienced (5+ yrs)", "Specialized/medical background"];
const SPECIAL_PREFS = ["Memory support (e.g. Dementia / Alzheimer's)", "Mobility assistance", "Medication reminders", "Meal preparation", "Companionship", "Comfort care companionship (e.g. end-of-life)", "Short-term recovery support (e.g. post-surgery)", "Light housekeeping", "Transportation / errands"];
const TOTAL = 8;
const DRAFT_KEY = "careos.intake.draft";
const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
const phoneValid = (p) => (p || "").replace(/\D/g, "").length >= 10;

function StepWrap({ title, children }) { return <div><h2 className="step-title">{title}</h2>{children}</div>; }
const label = (t) => <div className="section-label">{t}</div>;

export default function Intake() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ recipient_for: "loved_one", schedule: [] });
  const [zipCheck, setZipCheck] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMsg, setHelpMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [changeMsg, setChangeMsg] = useState(null);
  const [emailSent, setEmailSent] = useState(true);
  const [code, setCode] = useState("");
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [navErr, setNavErr] = useState(null);

  // Always start intake fresh — a shared/kiosk device at an event must not prefill the
  // previous person's answers. Clear any saved draft on mount and don't persist.
  useEffect(() => { try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ } }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleSchedule = (v) => setData((d) => ({ ...d, schedule: d.schedule.includes(v) ? d.schedule.filter((x) => x !== v) : [...d.schedule, v] }));
  const toggleArr = (k, v) => setData((d) => { const cur = d[k] || []; return { ...d, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }; });

  const validateZip = async (z) => {
    set("zip", z);
    if (z.length === 5) { try { setZipCheck(await api.get(`/service-area/validate?zip=${z}`, false)); } catch { setZipCheck(null); } }
    else setZipCheck(null);
  };

  const requestSpecialist = async () => {
    setHelpOpen(true);
    try {
      const r = await api.post("/intake/specialist-request", { full_name: data.full_name, phone: data.phone, step: `step_${step}`, partial_answers: { care_type: data.care_type, urgency: data.urgency, zip: data.zip } }, false);
      setHelpMsg(r.message);
    } catch { setHelpMsg("Call us any time at (201) 555-2273 — a specialist will help."); }
  };

  const stepError = (s) => {
    switch (s) {
      case 1: return data.care_type ? null : "Select the type of care you need.";
      case 2: return data.urgency ? null : "Tell us how soon care is needed.";
      case 3:
        if (!data.zip || data.zip.length !== 5) return "Enter a valid 5-digit ZIP code.";
        if (!(data.recipient_for === "self" || data.relationship_to_recipient)) return "Tell us your relationship to the care recipient.";
        if (!data.emergency_contact_name) return "Add an emergency contact name.";
        if (!phoneValid(data.emergency_contact_phone)) return "Add a valid emergency contact phone number.";
        if (data.care_type === "dementia" && !(data.family_contact_name && phoneValid(data.family_contact_phone))) return "Dementia care requires a family contact name and valid phone.";
        return null;
      case 4: return data.prior ? null : "Let us know about any prior provider.";
      case 5: return null;
      case 6:
        if (data.schedule.length === 0) return "Select at least one schedule block.";
        if (!data.budget_band) return "Choose a budget range.";
        return null;
      case 7: return null;
      case 8:
        if (!data.full_name) return "Enter your full name.";
        if (!emailValid(data.email)) return "Enter a valid email address.";
        if (!phoneValid(data.phone)) return "Enter a valid phone number.";
        if (!data.password || data.password.length < 8) return "Create a password of at least 8 characters.";
        if (data.password !== data.password_confirm) return "Passwords do not match.";
        if (!data.saved_credentials) return "Confirm you've saved your credentials.";
        if (!data.info_sharing_consent) return "Check the information-sharing consent to continue.";
        return null;
      default: return "Please complete this step.";
    }
  };

  const back = () => { if (step > 1) setStep(step - 1); else nav(-1); };
  const next = () => {
    const e = stepError(step);
    if (e) { setNavErr(e); return; }
    setNavErr(null);
    if (step < TOTAL) setStep(step + 1); else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        care_type: data.care_type, urgency: data.urgency, recipient_for: data.recipient_for,
        relationship_to_recipient: data.relationship_to_recipient, recipient_gender: data.recipient_gender,
        recipient_age_band: data.recipient_age_band, zip: data.zip, care_type_confirmed: true,
        prior_provider: data.prior === "previous" ? data.prior_provider : null, prior_reason: data.prior_reason,
        budget_band: data.budget_band, schedule: data.schedule, hours_per_week: data.hours_per_week,
        days_needed: data.days_needed || [], start_time: data.start_time, end_time: data.end_time,
        preferred_start_date: data.preferred_start_date, preferred_language: data.preferred_language,
        language_required: !!data.language_required,
        preferred_caregiver_characteristics: (data.characteristics || []).join(", ") || data.preferred_caregiver_characteristics,
        relevant_experience: data.relevant_experience, special_preferences: (data.special_prefs || []).join(", "),
        full_name: data.full_name, email: data.email, phone: data.phone, password: data.password,
        referral_source: data.referral_source, referral_code: data.referral_code,
        emergency_contact_name: data.emergency_contact_name, emergency_contact_phone: data.emergency_contact_phone,
        family_contact_name: data.family_contact_name, family_contact_phone: data.family_contact_phone,
        alt_contact_name: data.alt_contact_name, alt_contact_phone: data.alt_contact_phone,
        info_sharing_consent: data.info_sharing_consent,
        origin_url: window.location.origin,
      };
      const res = await api.post("/intake/submit", payload, false);
      localStorage.removeItem(DRAFT_KEY);
      if (res.account_created) { setEmailSent(res.email_sent !== false); setSubmitted(true); return; }
      nav("/intake-done", { replace: true, state: { result: res } });
    } catch (e) { setHelpOpen(true); setHelpMsg(e?.message || "Something went wrong submitting your intake. Please try again."); setSubmitting(false); }
  };

  const verifyCode = async () => {
    setVerifyMsg(null); setVerifying(true);
    try {
      await api.post("/auth/verify-email-code", { email: data.email, code: code.trim() }, false);
      nav("/login", { replace: true, state: { verified: true } });
    } catch (e) { setVerifyMsg(e?.message || "That code didn't work. Check it and try again."); }
    finally { setVerifying(false); }
  };

  const resend = async () => {
    setResendMsg(null);
    try {
      const r = await api.post("/auth/resend-verification", { email: data.email, origin_url: window.location.origin }, false);
      setEmailSent(r.email_sent !== false);
      setResendMsg(r.email_sent !== false ? "Verification email resent. Please check your inbox (and spam)." : "Our email service is temporarily unavailable — please try again in a minute.");
    } catch { setResendMsg("Couldn't resend right now — please try again shortly."); }
  };

  const changeEmail = async () => {
    setChangeMsg(null);
    if (!emailValid(newEmail)) { setChangeMsg("Enter a valid email address."); return; }
    try {
      const r = await api.post("/auth/change-unverified-email", { email: data.email, password: data.password, new_email: newEmail.trim(), origin_url: window.location.origin }, false);
      set("email", r.email || newEmail.trim());
      setEditingEmail(false); setNewEmail(""); setResendMsg(null); setEmailSent(r.email_sent !== false);
      setChangeMsg("Email updated — we've sent a new verification link.");
    } catch (e) { setChangeMsg(e?.message || "Couldn't update the email. Please try again."); }
  };

  if (submitted) {
    return (
      <ImmersiveShell showFooter={false}>
        <section className="imm-wrap" data-testid="intake-verify-pending">
          <div className="imm-legal">
            <div className="imm-contact-done" style={{ paddingTop: 12 }}>
              <Icon name="check-circle" size={48} color="var(--success)" />
              <h3>Intake received — verify your email</h3>
              {emailSent ? <>
                <p><b>Step 1:</b> We sent a verification link and 6-digit code to <b>{data.email}</b>. Open your inbox (check spam too) and tap the link — or enter the code on the verify page — to activate your account.</p>
                <p style={{ marginTop: 10 }}><b>Step 2:</b> Once verified, sign in with your email and password to complete your deposit and schedule your assessment in the client portal.</p>
              </> : <>
                <div className="banner" data-testid="email-not-sent" style={{ marginBottom: 8 }}>Your account was created, but we couldn't send the verification email just now. Tap <b>Resend</b> below, or fix your email if it's wrong.</div>
                <p>Verify to activate <b>{data.email}</b>, then sign in with your email and password.</p>
              </>}
              {resendMsg ? <div style={{ margin: "10px 0" }}><Badge testID="resend-msg" label={resendMsg} tone="success" /></div> : null}
              {changeMsg ? <div style={{ margin: "10px 0" }}><Badge testID="change-msg" label={changeMsg} tone={/updated/.test(changeMsg) ? "success" : "warning"} /></div> : null}
              <div style={{ maxWidth: 380, margin: "16px auto 0", width: "100%", textAlign: "left" }}>
                <Field testID="verify-code-input" label="Enter the 6-digit code from your email" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" maxLength={6} autoFocus onEnter={() => { if (code.trim().length >= 6) verifyCode(); }} />
                {verifyMsg ? <div style={{ margin: "6px 0" }}><Badge testID="verify-msg" label={verifyMsg} tone="warning" /></div> : null}
                <Button testID="verify-code-submit" icon="check" label="Verify & go to sign in" onClick={verifyCode} loading={verifying} disabled={code.trim().length < 6} />
                <div style={{ height: 12 }} />
                {editingEmail ? (
                  <div data-testid="change-email-form">
                    <Field testID="new-email" label="Corrected email address" value={newEmail} onChange={setNewEmail} placeholder="you@example.com" />
                    <Button testID="save-email" label="Update email & resend link" onClick={changeEmail} />
                    <div style={{ height: 8 }} />
                    <Button testID="cancel-email" variant="ghost" label="Cancel" onClick={() => { setEditingEmail(false); setChangeMsg(null); }} />
                  </div>
                ) : (
                  <button className="linkish" data-testid="wrong-email" onClick={() => { setEditingEmail(true); setNewEmail(""); }} style={{ background: "none", border: "none", color: "var(--imm-sky)", cursor: "pointer", fontSize: 14, textDecoration: "underline", padding: "4px 0" }}>Entered the wrong email? Change it</button>
                )}
                <div style={{ height: 8 }} />
                <Button testID="resend-verify" variant="secondary" icon="refresh-cw" label="Resend verification email" onClick={resend} />
                <div style={{ height: 8 }} />
                <Button testID="go-login" variant="ghost" label="Go to sign in" onClick={() => nav("/login")} />
              </div>
            </div>
          </div>
        </section>
      </ImmersiveShell>
    );
  }


  return (
    <div className="screen" data-testid="intake-screen">
      <div className="container-narrow">
        <PageHeader title="Care intake" subtitle="HomecareMatches" onBack={back}
          right={<Button small variant="secondary" testID="specialist-escape" icon="phone-call" label="Specialist" onClick={requestSpecialist} />} />
        <ProgressBar current={step} total={TOTAL} />
        {helpOpen && <div className="banner" data-testid="specialist-banner">{helpMsg || "Connecting you to a specialist — a routed request is being created. You never have to finish alone."}</div>}

        {step === 1 && <StepWrap title="What kind of care do you need?">
          {CARE_TYPES.map((c) => <SelectCard key={c.v} testID={`care-${c.v}`} title={c.t} desc={c.d} selected={data.care_type === c.v} onClick={() => set("care_type", c.v)} />)}
        </StepWrap>}

        {step === 2 && <StepWrap title="How soon do you need to start?">
          {URGENCY.map((u) => <SelectCard key={u.v} testID={`urgency-${u.v}`} title={u.t} desc={u.d} selected={data.urgency === u.v} onClick={() => set("urgency", u.v)} />)}
        </StepWrap>}

        {step === 3 && <StepWrap title="Who needs care?">
          <div className="chip-wrap" style={{ marginBottom: 12 }}>
            <Chip testID="for-loved" label="For a loved one" selected={data.recipient_for === "loved_one"} onClick={() => set("recipient_for", "loved_one")} />
            <Chip testID="for-self" label="For myself" selected={data.recipient_for === "self"} onClick={() => set("recipient_for", "self")} />
          </div>
          {data.recipient_for === "loved_one" && <>
            {label("RELATIONSHIP")}<div className="chip-wrap">{RELATIONSHIPS.map((r) => <Chip key={r} label={r} selected={data.relationship_to_recipient === r} onClick={() => set("relationship_to_recipient", r)} />)}</div>
            {label("GENDER")}<div className="chip-wrap">{GENDERS.map((g) => <Chip key={g} label={g} selected={data.recipient_gender === g} onClick={() => set("recipient_gender", g)} />)}</div>
            {label("AGE")}<div className="chip-wrap">{AGE_BANDS.map((a) => <Chip key={a} label={a} selected={data.recipient_age_band === a} onClick={() => set("recipient_age_band", a)} />)}</div>
          </>}
          <div style={{ marginTop: 12 }}>
            <Field testID="zip-input" label="ZIP code" value={data.zip || ""} onChange={validateZip} placeholder="07030" inputMode="numeric" maxLength={5} />
            {zipCheck && <Badge testID="zip-status" label={zipCheck.message} tone={zipCheck.in_service_area ? "success" : "warning"} />}
          </div>
          {data.recipient_for === "self" ? <div style={{ marginTop: 12 }}>
            {label("RESPONSIBLE PARTY (EMERGENCY CONTACT) — REQUIRED")}
            <p className="note">Someone we can reach in an emergency. We'll call them to ask permission before sharing updates.</p>
            <Field testID="emergency-name" label="Contact name" value={data.emergency_contact_name || ""} onChange={(v) => set("emergency_contact_name", v)} placeholder="Full name" />
            <Field testID="emergency-phone" label="Contact phone" value={data.emergency_contact_phone || ""} onChange={(v) => set("emergency_contact_phone", v)} placeholder="(201) 555-0100" />
          </div> : <div style={{ marginTop: 12 }}>
            {label("EMERGENCY CONTACT — REQUIRED")}
            <p className="note">Someone we can reach in an emergency about this care.</p>
            <Field testID="emergency-name" label="Contact name" value={data.emergency_contact_name || ""} onChange={(v) => set("emergency_contact_name", v)} placeholder="Full name" />
            <Field testID="emergency-phone" label="Contact phone" value={data.emergency_contact_phone || ""} onChange={(v) => set("emergency_contact_phone", v)} placeholder="(201) 555-0100" />
            {label("ALTERNATE CONTACT (OPTIONAL)")}
            <Field testID="alt-name" label="Alternate contact name" value={data.alt_contact_name || ""} onChange={(v) => set("alt_contact_name", v)} placeholder="Full name (optional)" />
            <Field testID="alt-phone" label="Alternate contact phone" value={data.alt_contact_phone || ""} onChange={(v) => set("alt_contact_phone", v)} placeholder="(201) 555-0100 (optional)" />
          </div>}
          {data.care_type === "dementia" ? <div style={{ marginTop: 12 }} data-testid="memory-contact">
            {label("FAMILY MEMBER FOR DAY 1 (MEMORY SUPPORT) — REQUIRED")}
            <p className="note">For memory-support care, a family member must be reachable on the first day of service.</p>
            <Field testID="family-name" label="Family member name" value={data.family_contact_name || ""} onChange={(v) => set("family_contact_name", v)} placeholder="Full name" />
            <Field testID="family-phone" label="Family member phone" value={data.family_contact_phone || ""} onChange={(v) => set("family_contact_phone", v)} placeholder="(201) 555-0100" />
          </div> : null}
        </StepWrap>}

        {step === 4 && <StepWrap title="Have you used home care before?">
          <SelectCard testID="prior-first" title="This is our first time" selected={data.prior === "first_time"} onClick={() => set("prior", "first_time")} />
          <SelectCard testID="prior-previous" title="We had a previous provider" selected={data.prior === "previous"} onClick={() => set("prior", "previous")} />
          {data.prior === "previous" && <div style={{ marginTop: 12 }}>
            <Field label="Previous provider (optional)" value={data.prior_provider || ""} onChange={(v) => set("prior_provider", v)} placeholder="Provider name" />
            <Field label="Reason for leaving (optional)" value={data.prior_reason || ""} onChange={(v) => set("prior_reason", v)} placeholder="What went wrong?" multiline />
          </div>}
        </StepWrap>}

        {step === 5 && <StepWrap title="Confirm the care type">
          <Card><div className="tag" style={{ color: "var(--success)" }}>OUR RECOMMENDATION</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{CARE_TYPES.find((c) => c.v === data.care_type)?.t}</div>
            <p className="note">Based on your answers so far. You can revise it below.</p></Card>
          {label("REVISE IF NEEDED")}
          {CARE_TYPES.map((c) => <SelectCard key={c.v} testID={`confirm-${c.v}`} title={c.t} selected={data.care_type === c.v} onClick={() => set("care_type", c.v)} />)}
        </StepWrap>}

        {step === 6 && <StepWrap title="Budget and schedule">
          <Card><div className="free-banner"><Icon name="gift" size={16} />1 free day (up to 8 hours) — earned after your first 5 days of service. No commitment.</div>
            <p className="note">Pick the weekly budget you're comfortable with. Your rate is guaranteed to stay within it.</p></Card>
          {label("BUDGET BAND")}<div className="chip-wrap">{BUDGETS.map((b) => <Chip key={b} label={b} selected={data.budget_band === b} onClick={() => set("budget_band", b)} />)}</div>
          {label("PREFERRED SCHEDULE (multi-select)")}<div className="chip-wrap">{SCHEDULE.map((s) => <Chip key={s} testID={`sched-${s}`} label={s.replace(/_/g, " ")} selected={data.schedule.includes(s)} onClick={() => toggleSchedule(s)} />)}</div>
          {label("DAYS NEEDED (multi-select)")}<div className="chip-wrap">{DAYS.map((d) => <Chip key={d} testID={`day-${d}`} label={d} selected={(data.days_needed || []).includes(d)} onClick={() => toggleArr("days_needed", d)} />)}</div>
          {label("HOURS PER WEEK")}<div className="chip-wrap">{HOURS_PER_WEEK.map((h) => <Chip key={h} testID={`hpw-${h}`} label={h} selected={data.hours_per_week === h} onClick={() => set("hours_per_week", h)} />)}</div>
          {label("START TIME")}
          <TimePicker testID="start-time" value={data.start_time || null} onChange={(v) => set("start_time", v)} />
          {label("END TIME")}
          <TimePicker testID="end-time" value={data.end_time || null} onChange={(v) => set("end_time", v)} />
          {label("PREFERRED START DATE (OPTIONAL) — MIN 48 HOURS OUT")}
          <DatePicker testID="start-date" value={data.preferred_start_date || null} onChange={(v) => set("preferred_start_date", v)} minDate={new Date(Date.now() + 48 * 3600 * 1000)} />
          {data.preferred_start_date ? <p className="note">Selected: {new Date(data.preferred_start_date + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p> : null}
        </StepWrap>}

        {step === 7 && <StepWrap title="Care preferences & needs">
          <Card><p className="note" style={{ margin: 0 }}>These help our HomecareMatches team match you with an agency and caregiver who truly fit. All optional.</p></Card>
          {label("PREFERRED CAREGIVER CHARACTERISTICS (multi-select)")}<div className="chip-wrap">{CHARACTERISTICS.map((c) => <Chip key={c} testID={`char-${c}`} label={c} selected={(data.characteristics || []).includes(c)} onClick={() => toggleArr("characteristics", c)} />)}</div>
          {label("PREFERRED LANGUAGE")}
          <select className="select" data-testid="intake-language" value={data.preferred_language || ""} onChange={(e) => set("preferred_language", e.target.value)}>
            <option value="">Select a language…</option>
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="consent" data-testid="intake-language-required" onClick={() => set("language_required", !data.language_required)}>
            <Icon name={data.language_required ? "check-square" : "square"} size={22} color={data.language_required ? "var(--brand)" : "var(--border-strong)"} />
            <span className="ctext">This language is <b>strictly required</b> for the caregiver (uncheck if it's just a preference).</span>
          </div>
          {label("RELEVANT CAREGIVER EXPERIENCE")}{EXPERIENCE_LEVELS.map((e) => <SelectCard key={e} testID={`exp-${e}`} title={e} selected={data.relevant_experience === e} onClick={() => set("relevant_experience", e)} />)}
          {label("SPECIAL SERVICE PREFERENCES (multi-select)")}<div className="chip-wrap">{SPECIAL_PREFS.map((s) => <Chip key={s} testID={`pref-${s}`} label={s} selected={(data.special_prefs || []).includes(s)} onClick={() => toggleArr("special_prefs", s)} />)}</div>
        </StepWrap>}

        {step === 8 && <StepWrap title="Create your account">
          <Field testID="name-input" label="Full name" value={data.full_name || ""} onChange={(v) => set("full_name", v)} placeholder="Your name" />
          <Field testID="email-input" label="Email" value={data.email || ""} onChange={(v) => set("email", v)} placeholder="you@example.com" />
          <Field testID="phone-input" label="Phone" value={data.phone || ""} onChange={(v) => set("phone", v)} placeholder="(201) 555-0100" />
          {data.email && !emailValid(data.email) ? <p className="err-text">Enter a valid email address.</p> : null}
          {data.phone && !phoneValid(data.phone) ? <p className="err-text">Enter a valid phone number (at least 10 digits).</p> : null}
          <Field testID="password-input" label="Password (min 8 characters)" type="password" value={data.password || ""} onChange={(v) => set("password", v)} placeholder="••••••••" />
          <Field testID="password-confirm" label="Confirm password" type="password" value={data.password_confirm || ""} onChange={(v) => set("password_confirm", v)} placeholder="••••••••" />
          {data.password && data.password_confirm && data.password !== data.password_confirm ? <p className="note">Passwords don't match yet.</p> : null}
          <Field label="How did you hear about us? (optional)" value={data.referral_source || ""} onChange={(v) => set("referral_source", v)} placeholder="Google, friend, doctor…" />
          <div className="banner" data-testid="save-creds-note"><Icon name="key" size={16} /> Please write down your email and password now — you'll use them to sign in after verifying your email.</div>
          <div className="consent" data-testid="saved-credentials" onClick={() => set("saved_credentials", !data.saved_credentials)}>
            <Icon name={data.saved_credentials ? "check-square" : "square"} size={22} color={data.saved_credentials ? "var(--brand)" : "var(--border-strong)"} />
            <span className="ctext">I've saved my email and password somewhere safe.</span>
          </div>
          <div className="consent" data-testid="consent-share" onClick={() => set("info_sharing_consent", !data.info_sharing_consent)}>
            <Icon name={data.info_sharing_consent ? "check-square" : "square"} size={22} color={data.info_sharing_consent ? "var(--brand)" : "var(--border-strong)"} />
            <span className="ctext">I agree that HomecareMatches may share my request with a matched home care agency, who will contact me to arrange the assessment.</span>
          </div>
          <p className="note">Next: we'll email you a link to verify this address. After that, sign in with your email and password — no code needed.</p>
        </StepWrap>}

        <div style={{ marginTop: 20 }}>
          {navErr ? <p className="err-text" data-testid="step-error" style={{ marginBottom: 8 }}>{navErr}</p> : null}
          <Button testID="wizard-next" label={step === TOTAL ? "Create account & verify email" : "Continue"} onClick={next} loading={submitting} />
        </div>
      </div>
    </div>
  );
}
